import React, { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Building2, Network, MapPin, Eye } from 'lucide-react';
import { toast } from 'sonner';

// Fix for default marker icons in React-Leaflet
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
});

// Custom marker icons
const createCustomIcon = (color) => {
  return L.divIcon({
    className: 'custom-marker',
    html: `<div style="background-color: ${color}; width: 24px; height: 24px; border-radius: 50%; border: 3px solid white; box-shadow: 0 2px 6px rgba(0,0,0,0.3);"></div>`,
    iconSize: [24, 24],
    iconAnchor: [12, 12],
    popupAnchor: [0, -12]
  });
};

const deliveredIcon = createCustomIcon('#10b981'); // green
const pendingIcon = createCustomIcon('#f59e0b'); // amber

export default function MapView() {
  const { API } = useAuth();
  const navigate = useNavigate();
  const [circuits, setCircuits] = useState([]);
  const [customers, setCustomers] = useState({});
  const [loading, setLoading] = useState(true);
  const [mapCenter, setMapCenter] = useState([12.9716, 77.5946]); // Default: Bangalore
  const [stats, setStats] = useState({ total: 0, delivered: 0, pending: 0, withLocation: 0 });

  useEffect(() => {
    const fetchData = async () => {
      try {
        // Fetch all circuits
        const circuitsRes = await axios.get(`${API}/circuits`);
        const circuitsData = circuitsRes.data;
        setCircuits(circuitsData);

        // Calculate stats
        const circuitsWithLocation = circuitsData.filter(c => c.latitude && c.longitude);
        setStats({
          total: circuitsData.length,
          delivered: circuitsData.filter(c => c.is_delivered).length,
          pending: circuitsData.filter(c => !c.is_delivered).length,
          withLocation: circuitsWithLocation.length
        });

        // Set map center to first circuit with location
        if (circuitsWithLocation.length > 0) {
          setMapCenter([circuitsWithLocation[0].latitude, circuitsWithLocation[0].longitude]);
        }

        // Fetch customer names
        const customerIds = [...new Set(circuitsData.map(c => c.customer_id))];
        const customerMap = {};
        for (const customerId of customerIds) {
          try {
            const custRes = await axios.get(`${API}/customers/${customerId}`);
            customerMap[customerId] = custRes.data;
          } catch (e) {
            customerMap[customerId] = { company_name: 'Unknown' };
          }
        }
        setCustomers(customerMap);
      } catch (error) {
        toast.error('Failed to fetch map data');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [API]);

  const circuitsWithLocation = circuits.filter(c => c.latitude && c.longitude);

  return (
    <div className="space-y-6" data-testid="map-view-page">
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-3xl font-semibold text-slate-900 tracking-tight">Circuit Map View</h2>
          <p className="text-slate-600 mt-1">View all circuit locations on map</p>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-slate-100 rounded-lg">
                <Network className="h-5 w-5 text-slate-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-900">{stats.total}</p>
                <p className="text-sm text-slate-500">Total Circuits</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-emerald-100 rounded-lg">
                <Network className="h-5 w-5 text-emerald-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-emerald-600">{stats.delivered}</p>
                <p className="text-sm text-slate-500">Delivered</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-amber-100 rounded-lg">
                <Network className="h-5 w-5 text-amber-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-amber-600">{stats.pending}</p>
                <p className="text-sm text-slate-500">Pending</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-sky-100 rounded-lg">
                <MapPin className="h-5 w-5 text-sky-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-sky-600">{stats.withLocation}</p>
                <p className="text-sm text-slate-500">On Map</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Map */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <MapPin className="h-5 w-5" /> Circuit Locations
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {loading ? (
            <div className="h-[500px] flex items-center justify-center bg-slate-50">
              <p className="text-slate-500">Loading map...</p>
            </div>
          ) : circuitsWithLocation.length === 0 ? (
            <div className="h-[500px] flex flex-col items-center justify-center bg-slate-50">
              <MapPin className="h-16 w-16 text-slate-300 mb-4" />
              <p className="text-slate-500 text-lg">No circuits with location data</p>
              <p className="text-slate-400 text-sm mt-1">Add latitude and longitude to circuits to see them on the map</p>
            </div>
          ) : (
            <div className="h-[500px] rounded-b-lg overflow-hidden">
              <MapContainer
                center={mapCenter}
                zoom={10}
                style={{ height: '100%', width: '100%' }}
              >
                <TileLayer
                  attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                  url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                />
                {circuitsWithLocation.map((circuit) => {
                  const customer = customers[circuit.customer_id];
                  return (
                    <Marker
                      key={circuit.circuit_id}
                      position={[circuit.latitude, circuit.longitude]}
                      icon={circuit.is_delivered ? deliveredIcon : pendingIcon}
                    >
                      <Popup>
                        <div className="min-w-[250px]">
                          <div className="font-bold text-slate-900 mb-1">{circuit.location_name}</div>
                          <div className="text-xs text-slate-500 mb-2">{circuit.ant_circuit_id}</div>
                          
                          <div className="flex items-center gap-2 mb-2">
                            <Building2 className="h-4 w-4 text-slate-400" />
                            <span className="text-sm">{customer?.company_name || 'Unknown'}</span>
                          </div>
                          
                          <div className="text-xs text-slate-600 mb-2">
                            {circuit.installation_address}, {circuit.city}
                          </div>
                          
                          <div className="flex items-center justify-between mb-2">
                            <span className="text-sm font-medium">{circuit.bandwidth} Mbps</span>
                            <Badge className={circuit.is_delivered ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'}>
                              {circuit.is_delivered ? 'Delivered' : 'Pending'}
                            </Badge>
                          </div>
                          
                          <Button 
                            size="sm" 
                            className="w-full mt-2"
                            onClick={() => navigate(`/customers/${circuit.customer_id}`)}
                          >
                            <Eye className="h-3 w-3 mr-1" /> View Customer
                          </Button>
                        </div>
                      </Popup>
                    </Marker>
                  );
                })}
              </MapContainer>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Legend */}
      <Card>
        <CardContent className="p-4">
          <div className="flex items-center gap-6">
            <span className="text-sm font-medium text-slate-700">Legend:</span>
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 rounded-full bg-emerald-500 border-2 border-white shadow"></div>
              <span className="text-sm text-slate-600">Delivered</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 rounded-full bg-amber-500 border-2 border-white shadow"></div>
              <span className="text-sm text-slate-600">Pending</span>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
